import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "full-height" }
const _hoisted_2 = { class: "d-flex justify-center justify-self-end" }
const _hoisted_3 = { class: "text-lg-body-1 text--error" }
const _hoisted_4 = { class: "pr-2" }
const _hoisted_5 = { class: "pl-2" }
const _hoisted_6 = { class: "mt-4 text--error" }

import { computed, reactive, watch } from 'vue';
import { createSheet } from '@/services/workbookService';
import PrimaryButton from '@/components/buttons/PrimaryButton.vue';
import ProgressDialog from '@/components/dialogs/ProgressDialog.vue';
import { NovaApiService } from '@/services/nova/NovaApiService';
import { useStore } from 'vuex';
import { GenericArrayOfObjects } from '@/interfaces/api';
import { debounce } from 'ts-debounce';
import {
  cleanWhiteSpace,
  deleteProperty,
  emailToFormattedArray,
  getProperty,
  keyCompaniesByName,
  objHasProperty,
  systemNotify
} from '@/services/util';
import { read, utils, WorkBook, writeFileXLSX } from 'xlsx-js-style';
import {
  INovaAppointment,
  INovaCarrier,
  INovaCarrierData,
  INovaDock,
  INovaLoadType,
  INovaSchedule,
  INovaUser,
  INovaWarehouse
} from '@/interfaces/nova';
import { DateTime } from 'luxon';
import { useProgress } from '@/composables/progressBar';
import { removeInvalidCustomFieldsKeys } from '@/services/appointment_plus/APNovaMap';
import { UserRole } from '../../../../../libs/core/src';
import { isUUID } from 'class-validator';

const CONFIRM_TEXT = 'CONFIRM';
interface IState {
  account: string;
  key: string;
  orgs: GenericArrayOfObjects;
  users: GenericArrayOfObjects;
  selectedOrg: { [key: string]: any } | null;
  selectedUser: { [key: string]: any } | null;
  orgSearch: string;
  file: File | null;
  isLoading: {
    orgs: boolean;
    users: boolean;
    import: boolean;
  };
  shouldShowGate: boolean;
  importEntityCounts: GenericArrayOfObjects;
  confirmTextInput: string;
  fileRenderKey: number; // Workaround for Vuetify 3 bug in file input (it's still in Beta but almost completed)
  warehouseCustomFields: { [key: string]: any } | null;
  warehouseReport: GenericArrayOfObjects;
}

const ALREADY_EXISTS_MESSAGE = 'N/A (already exists in Nova)';
const SUCCESS_IMPORT_MESSAGE = 'Success';

interface IImportParams {
  file: File;
  token: string;
  org: { [key: string]: any };
}

export type failureStatusMessageParamsError = {
  e: any;
  message?: never;
};

export type failureStatusMessageParamsMessage = {
  e?: never;
  message: string;
};

type WarehouseImportParams = {
  warehouses: INovaWarehouse[];
  apiService: NovaApiService;
  existingWarehouses: GenericArrayOfObjects;
};

type LoadTypeImportParams = {
  orgId: string;
  loadTypes: INovaLoadType[];
  apiService: NovaApiService;
  warehousesKeyedByExternalId: { [key: string]: any };
};

type DockImportParams = {
  orgId: string;
  docks: INovaDock[];
  apiService: NovaApiService;
  loadTypeIdsKeyedByExternalId: { [key: string]: string };
  warehousesKeyedByExternalId: { [key: string]: INovaWarehouse };
};


export default /*@__PURE__*/_defineComponent({
  __name: 'NovaImportForm',
  setup(__props) {

const IMPORT_ENTITIES = ['Warehouses', 'LoadTypes', 'Docks', 'Appointments', 'Users', 'Carriers'];
const store = useStore();

const state: IState = reactive({
  account: '',
  key: '',
  orgs: [],
  users: [],
  selectedOrg: null,
  selectedUser: null,
  orgSearch: '',
  file: null,
  isLoading: {
    orgs: false,
    users: false,
    import: false
  },
  shouldShowGate: false,
  importEntityCounts: [],
  confirmTextInput: '',
  fileRenderKey: 0,
  warehouseCustomFields: null,
  warehouseReport: []
});

const progress = reactive(useProgress());

const novaApiService = new NovaApiService({ token: store.getters.accessToken });
const importData = async () => {
  if (!isValid() || !state.selectedOrg || !state.file) {
    return;
  }
  closeGateDialog();
  try {
    state.isLoading.import = true;
    try {
      const response = await novaApiService.loginAs(state.selectedUser?.id as string);
      const userToken = getProperty(response.data[0], 'access_token');
      await importWorkbookToNova({ file: state.file, token: userToken, org: state.selectedOrg });
    } catch (e) {
      abortImportProcess();
      throw new Error(getErrorMessage(e));
    }
  } catch (e: any) {
    systemNotify({
      message: e.message ?? e?.response?.data?.message ?? 'An unknown error occurred',
      type: 'error'
    });
    abortImportProcess();
    return false;
  } finally {
    state.warehouseCustomFields = null;
    state.file = null;
    state.selectedOrg = null;
    state.selectedUser = null;
    errorMessages.file = '';
    errorMessages.org = '';
    errorMessages.user = '';
    state.isLoading.import = false;
    state.fileRenderKey++;
  }
};

async function importWorkbookToNova({ file, token, org }: IImportParams) {
  state.warehouseReport = [];
  state.warehouseCustomFields = null;
  progress.setDialogVisibility(true);

  const COMPANY_LIMIT = 150000;
  const data = await file.arrayBuffer();
  const workbookToImport: WorkBook = read(data);
  const workbookReport: WorkBook = utils.book_new();

  const orgSettings = org.settings ?? {};
  const initialMuteNotificationsSetting = orgSettings.muteAppointmentNotifications ?? false;
  const apiService = new NovaApiService({
    token: token
  });

  try {
    let orgId = null;

    // Retrieve selected user's org ID and verify org matches
    try {
      orgId = await getSelectedUserOrgId(apiService);
      if (orgId !== org.id) {
        throw new Error(
          getFailureStatusMessage({ message: "Org ID does not match selected User's Org ID" })
        );
      }
    } catch (e: any) {
      abortImportProcess();
      throw new Error(getErrorMessage(e));
    }

    const warehouses = utils.sheet_to_json(
      workbookToImport.Sheets['Warehouses']
    ) as INovaWarehouse[];
    let users = utils.sheet_to_json(workbookToImport.Sheets['Users']) as INovaUser[];
    let carriers = utils.sheet_to_json(workbookToImport.Sheets['Carriers']) as INovaCarrierData[];

    const skipWarehouseImports = warehouses.length === 0;
    const skipUserImports = users.length === 0;
    const skipCarrierImports = carriers.length === 0;

    // If no data to import
    if (skipWarehouseImports && skipUserImports && skipCarrierImports) {
      abortImportProcess();
      throw new Error(getFailureStatusMessage({ message: 'No Data to Import' }));
    }

    const existingWarehousesRes = await apiService.getWarehouses();
    const existingWarehouses = existingWarehousesRes.data ?? [];

    if (skipWarehouseImports && !skipUserImports) {
      const endingIntervalVal = skipCarrierImports ? 98 : 5;
      // User imports only - no warehouse or carrier data
      progress.createInterval('Inviting Users', endingIntervalVal);
      const { usersReport } = await inviteUsers(apiService, users, existingWarehouses);
      createSheet(workbookReport, usersReport, 'Users');
      if (skipCarrierImports) {
        await wrapUp(workbookReport);
        return;
      }
    }

    let companiesKeyedByName: { [key: string]: any } = {};
    try {
      const companyRes = await apiService.getCompanies({ limit: COMPANY_LIMIT });
      companiesKeyedByName = keyCompaniesByName(companyRes.data);
    } catch (e: any) {
      abortImportProcess();
      throw new Error(getErrorMessage(e));
    }

    if (!skipCarrierImports) {
      const endingIntervalVal = skipWarehouseImports ? 98 : 10;
      // Carrier imports
      progress.createInterval('Creating Carriers', endingIntervalVal);
      const { carriersReport } = await createCarriers(apiService, carriers, companiesKeyedByName);
      createSheet(workbookReport, carriersReport, 'Carriers');
      if (skipWarehouseImports) {
        await wrapUp(workbookReport);
        return;
      }
    }

    // I don't think this is needed, but I'm adding as an absolute fallback
    if (skipWarehouseImports) {
      await wrapUp(workbookReport);
      return;
    }

    try {
      // Set appointment notification setting to "false"
      const newOrgSettings = { ...orgSettings, muteAppointmentNotifications: true };
      const orgUpdateResponse = (await apiService.updateOrg(orgId, {
        ...org,
        settings: newOrgSettings
      })) as { [key: string]: any };
      if (!orgUpdateResponse.data?.[0]?.settings?.muteAppointmentNotifications) {
        abortImportProcess();
        throw new Error(
          getFailureStatusMessage({
            message: 'Muting Org Appointment Notifications was unsuccessful'
          })
        );
      }
    } catch (e: any) {
      abortImportProcess();
      throw new Error(getErrorMessage(e));
    }

    // WAREHOUSES
    progress.createInterval('Importing Warehouses', 20);
    const { warehousesKeyedByExternalId } = await importWarehouses({
      warehouses,
      apiService,
      existingWarehouses
    });
    createSheet(workbookReport, state.warehouseReport, 'Warehouses');

    // LOAD TYPES
    await progress.zoom(20);
    progress.createInterval('Importing Load Types', 30);
    const loadTypes = utils.sheet_to_json(workbookToImport.Sheets['LoadTypes']) as INovaLoadType[];
    const { loadTypeIdsKeyedByExternalId, loadTypesReport } = await importLoadTypes({
      orgId,
      loadTypes,
      apiService,
      warehousesKeyedByExternalId
    });
    createSheet(workbookReport, loadTypesReport, 'LoadTypes');

    // DOCKS
    await progress.zoom(30);
    progress.createInterval('Importing Docks', 50);
    const docks = utils.sheet_to_json(workbookToImport.Sheets['Docks']) as INovaDock[];
    const { docksKeyedByExternalId, docksReport } = await importDocks({
      orgId,
      docks,
      apiService,
      loadTypeIdsKeyedByExternalId,
      warehousesKeyedByExternalId
    });
    createSheet(workbookReport, docksReport, 'Docks');

    // APPOINTMENTS
    const appointments = utils.sheet_to_json(
      workbookToImport.Sheets['Appointments']
    ) as INovaAppointment[];
    const appointmentsReport = [];

    if (appointments.length) {
      await progress.zoom(50);
      progress.createInterval('Importing Appointments', 95);
      const carriersKeyedByEmail: { [key: string]: any } = [];
      for (const appointment of appointments) {
        const isReserve = (appointment.type as string)?.toLowerCase() === 'reserve';

        if (
          appointment.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE ||
          appointment.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE
        ) {
          appointment.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
          appointmentsReport.push(appointment);
          continue;
        }
        if (!appointment.externalCompanyName && !isReserve) {
          appointment.wasImportedIntoNova = getFailureStatusMessage({
            message: 'Appointment is missing company name'
          });
          appointmentsReport.unshift(appointment);
          continue;
        }
        if (!appointment.email && !isReserve) {
          appointment.wasImportedIntoNova = getFailureStatusMessage({
            message: 'Appointment is missing carrier email'
          });
          appointmentsReport.unshift(appointment);
          continue;
        }
        if (!appointment.firstName) {
          appointment.wasImportedIntoNova = getFailureStatusMessage({
            message: 'Appointment is missing carrier first name'
          });
          appointmentsReport.unshift(appointment);
          continue;
        }
        if (!loadTypeIdsKeyedByExternalId[appointment.externalLoadTypeId] && !isReserve) {
          appointment.wasImportedIntoNova = getFailureStatusMessage({
            message: `Appointment's Load Type (${appointment.externalLoadTypeId}) failed to import - see LoadTypes tab for details`
          });
          appointmentsReport.unshift(appointment);
          continue;
        }

        // Handle appointment company
        let company;
        if (!isReserve) {
          company = companiesKeyedByName[appointment.externalCompanyName as string];
        }

        if (!company && !isReserve) {
          try {
            const companyRes = await apiService.createCompany(
              appointment.externalCompanyName as string
            );
            if (companyRes.data) {
              company = companyRes.data[0];
              companiesKeyedByName[appointment.externalCompanyName as string] = company;
            }
          } catch (e: any) {
            appointment.wasImportedIntoNova = getFailureStatusMessage({
              message: `appointment company creation failed because ${getErrorMessage(e)}`
            });
            appointmentsReport.unshift(appointment);
            continue;
          }
        }

        // Handle appointment carrier
        const appointmentEmails = Array.from(new Set(emailToFormattedArray(appointment.email)));
        appointment.email = appointmentEmails[0];

        appointment.ccEmails = getAppointmentCcEmails(appointment, appointmentEmails);

        if (!carriersKeyedByEmail[appointment.email] && !isReserve) {
          const carrierResponse = await apiService.getCarriers({
            s: {
              email: {
                $eq: appointment.email
              }
            }
          });
          if (carrierResponse?.data.length) {
            carriersKeyedByEmail[appointment.email] = carrierResponse.data[0];
          }
        }

        let carrier = carriersKeyedByEmail[appointment.email];
        if (!carrier && !isReserve) {
          try {
            // Create carrier
            const carrierData = {
              email: appointment.email,
              firstName: appointment.firstName ?? 'NO_FIRST_NAME',
              lastName: appointment.lastName ?? 'NO_LAST_NAME',
              companyId: company.id,
              phone: appointment.phone
            };
            if (!appointment.phone) {
              delete carrierData.phone;
            }
            const createCarrierRes = await apiService.createCarrier(carrierData);
            carrier = createCarrierRes?.data[0] as unknown as INovaCarrier;
            carriersKeyedByEmail[appointment.email.toLowerCase()] = carrier;
          } catch (e: any) {
            const reason = getErrorMessage(e).includes('duplicate key')
              ? 'email already exists as a non-carrier'
              : getErrorMessage(e);
            appointment.wasImportedIntoNova = getFailureStatusMessage({
              message: `appointment carrier creation failed because ${reason}`
            });
            appointmentsReport.unshift(appointment);
            continue;
          }
        }

        // Create appointment
        try {
          const apptWarehouse = warehousesKeyedByExternalId[appointment.externalWarehouseId];
          if (apptWarehouse) {
            const dateOpts = { zone: apptWarehouse.timezone };
            if (!docksKeyedByExternalId[appointment.externalDockId]?.id) {
              appointment.wasImportedIntoNova = getFailureStatusMessage({
                message: `Dock (${appointment.externalDockId}) was not imported - see Docks tab for details`
              });
              appointment.ccEmails = appointment.ccEmails.join(', ');
              appointmentsReport.unshift(appointment);
              continue;
            }
            if (appointment.tags && typeof appointment.tags === 'string') {
              appointment.tags = Array.from(
                new Set(appointment.tags.split(',').map(tag => cleanWhiteSpace(tag)))
              );
            }
            const appointmentData = {
              ...appointment,
              status: appointment.status?.toLowerCase() === 'requested' ? 'Requested' : 'Scheduled',
              userId: !isReserve ? carrier.id : appointment.userId,
              loadTypeId: !isReserve
                ? loadTypeIdsKeyedByExternalId[appointment.externalLoadTypeId]
                : null,
              dockId: docksKeyedByExternalId[appointment.externalDockId].id,
              orgId: orgId,
              start: DateTime.fromISO(appointment.start as string, dateOpts)
                .toUTC()
                .toISO(),
              end: DateTime.fromISO(appointment.end as string, dateOpts)
                .toUTC()
                .toISO(),
              customFields: appointment.customFields
                ? JSON.parse(appointment.customFields as string)
                : null
            };
            try {
              let apptResponse;
              if (isReserve) {
                apptResponse = await apiService.createReserve(appointmentData);
              } else {
                apptResponse = await apiService.createAppointment(appointmentData);
              }
              appointment.id = apptResponse?.data?.[0]?.id ?? '';
              appointment.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
              appointment.ccEmails = appointment.ccEmails.join(', ');
              appointment.tags = Array.isArray(appointment.tags)
                ? appointment.tags.join(', ')
                : appointment.tags;
              appointmentsReport.push(appointment);
            } catch (e: any) {
              appointment.wasImportedIntoNova = getFailureStatusMessage({ e });
              appointment.ccEmails = Array.isArray(appointment.ccEmails)
                ? appointment.ccEmails.join(', ')
                : '';
              appointment.tags = Array.isArray(appointment.tags)
                ? appointment.tags.join(', ')
                : appointment.tags;
              appointmentsReport.unshift(appointment);
            }
          } else {
            appointment.wasImportedIntoNova = getFailureStatusMessage({
              message: `Warehouse (${appointment.externalWarehouseId}) failed to import.  See Warehouses tab for details`
            });
            appointmentsReport.unshift(appointment);
            continue;
          }
        } catch (e: any) {
          appointment.wasImportedIntoNova = getFailureStatusMessage({ e });
          appointmentsReport.unshift(appointment);
          continue;
        }
      }
    }

    // Update dock schedules to original for newly imported docks only
    await progress.zoom(95);
    if (!skipUserImports) {
      progress.createInterval('Inviting Users', 98);
      const { usersReport } = await inviteUsers(apiService, users, existingWarehouses);
      createSheet(workbookReport, usersReport, 'Users');
    }
    progress.createInterval('Wrapping Up', 98);
    await Promise.all(
      docksReport
        .filter(dock => dock.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE && dock.id)
        .map(async dock => {
          await updateMaptsDockSchedule(apiService, dock, {
            version: 1,
            ...JSON.parse(dock.schedule as string)
          });
        })
    );

    createSheet(workbookReport, appointmentsReport, 'Appointments');

    await wrapUp(workbookReport);
  } catch (e: any) {
    abortImportProcess();
    let errorMessage = '';
    if (Object.keys(workbookReport.Sheets)?.length) {
      errorMessage += 'PARTIAL IMPORT - ';
      writeFileXLSX({ ...workbookToImport, ...workbookReport }, `partial-import report.xlsx`);
    }
    errorMessage += getErrorMessage(e);
    systemNotify({
      message: errorMessage,
      type: 'error'
    });
    return;
  } finally {
    // Set appointment notification setting to original setting
    const newOrgSettings = {
      ...orgSettings,
      muteAppointmentNotifications: initialMuteNotificationsSetting
    };
    await apiService.updateOrg(org.id, {
      ...org,
      settings: newOrgSettings
    });
    // Update warehouse Custom Fields to original on newly imported warehouses only
    await Promise.all(
      state.warehouseReport
        .filter(
          warehouse => warehouse.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE && warehouse.id
        )
        .map(async warehouse => {
          const fields = state.warehouseCustomFields?.[warehouse.id as string];
          // Only update warehouses where custom fields existed prior to import
          if (fields) {
            await updateWarehouseCustomFields(apiService, warehouse.id as string, fields);
          }
        })
    );

    state.warehouseCustomFields = null;
    state.warehouseReport = [];
  }
}

function getErrorMessage(e: any) {
  return e.response?.data?.message ?? e.message ?? 'An unknown error occurred';
}

async function wrapUp(workbookReport: any) {
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);

  writeFileXLSX(workbookReport, `report.xlsx`);
}

const abortImportProcess = () => {
  state.warehouseCustomFields = null;
  state.isLoading.import = false;
  progress.setDialogVisibility(false);
  progress.clear();
  progress.update('', 0);
};

function getFailureStatusMessage({
  e,
  message
}: failureStatusMessageParamsError | failureStatusMessageParamsMessage) {
  return `FAIL REASON: ${message ?? getErrorMessage(e)}`;
}

async function getSelectedUserOrgId(apiService: NovaApiService) {
  let loggedInAsUserResponse;
  try {
    loggedInAsUserResponse = await apiService.getLoggedInAsUser();
  } catch (e: any) {
    abortImportProcess();
    throw new Error(`Could not log in as selected user: ${getErrorMessage(e)}`);
  }
  return loggedInAsUserResponse.data[0].orgId;
}

async function importWarehouses({
  warehouses,
  apiService,
  existingWarehouses
}: WarehouseImportParams) {
  const warehousesKeyedByExternalId: { [key: string]: INovaWarehouse } = {};
  state.warehouseReport = [];
  for (const warehouse of warehouses) {
    if (warehouse?.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE) {
      warehouse.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
      state.warehouseReport.push(warehouse);
      warehousesKeyedByExternalId[warehouse.externalId] = warehouse;
      continue;
    }
    // TODO: Seeing pattern with checking existing entities here - explore abstraction
    const existingWarehouse = existingWarehouses.find(existingW => {
      return warehouse.name === existingW.name;
    });
    Object.keys(warehouse).forEach(key => {
      const propertyVal = getProperty(warehouse, key);
      if (typeof propertyVal !== 'boolean' && propertyVal !== 0 && !propertyVal) {
        deleteProperty(warehouse, key);
      }
    });
    if (warehouse.customApptFieldsTemplate) {
      warehouse.customApptFieldsTemplate = JSON.parse(warehouse.customApptFieldsTemplate as string);
      warehouse.customApptFieldsTemplate = removeInvalidCustomFieldsKeys(
        warehouse.customApptFieldsTemplate as GenericArrayOfObjects
      );
    }
    if (existingWarehouse) {
      warehouse.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
      warehouse.id = existingWarehouse.id;
      if (!state.warehouseCustomFields) {
        state.warehouseCustomFields = {};
      }
      if (!objHasProperty(state.warehouseCustomFields ?? {}, existingWarehouse.id)) {
        state.warehouseCustomFields[existingWarehouse.id] =
          existingWarehouse.customApptFieldsTemplate;
      }

      if (warehouse.externalId && existingWarehouse.id) {
        existingWarehouse.externalHeadquartersId = warehouse.externalHeadquartersId;
        warehousesKeyedByExternalId[warehouse.externalId] = existingWarehouse as INovaWarehouse;
      }
      if (warehouse.customApptFieldsTemplate) {
        await updateWarehouseCustomFields(
          apiService,
          existingWarehouse.id,
          warehouse.customApptFieldsTemplate as unknown as GenericArrayOfObjects
        );
      }

      if (warehouse.customApptFieldsTemplate) {
        warehouse.customApptFieldsTemplate = JSON.stringify(warehouse.customApptFieldsTemplate);
      }
      state.warehouseReport.push(warehouse);

      continue;
    }
    warehouse.schedule = JSON.parse(warehouse.schedule);
    if (warehouse.ppeRequirements) {
      warehouse.ppeRequirements = JSON.parse(warehouse.ppeRequirements as string);
    }
    if (warehouse.amenities) {
      warehouse.amenities = JSON.parse(warehouse.amenities as string);
    }
    try {
      const response = await apiService.createWarehouse(warehouse as INovaWarehouse);
      const newWarehouse = response.data[0] as unknown as INovaWarehouse;
      warehouse.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
      warehouse.id = newWarehouse.id;
      newWarehouse.externalHeadquartersId = warehouse.externalHeadquartersId;
      if (warehouse.externalId && newWarehouse.id) {
        warehousesKeyedByExternalId[warehouse.externalId] = warehouse;
      }
      warehouse.schedule = JSON.stringify(warehouse.schedule);
      warehouse.ppeRequirements = JSON.stringify(warehouse.ppeRequirements as string);
      warehouse.amenities = JSON.stringify(warehouse.amenities as string);
      if (warehouse.customApptFieldsTemplate) {
        warehouse.customApptFieldsTemplate = JSON.stringify(warehouse.customApptFieldsTemplate);
      }
      state.warehouseReport.push(warehouse);
    } catch (e: any) {
      warehouse.schedule = JSON.stringify(warehouse.schedule);
      warehouse.ppeRequirements = JSON.stringify(warehouse.ppeRequirements as string);
      warehouse.amenities = JSON.stringify(warehouse.amenities as string);
      if (warehouse.customApptFieldsTemplate) {
        warehouse.customApptFieldsTemplate = JSON.stringify(warehouse.customApptFieldsTemplate);
      }
      warehouse.wasImportedIntoNova = getFailureStatusMessage({ e });
      state.warehouseReport.unshift(warehouse);
    }
  }
  return { warehousesKeyedByExternalId };
}

async function importLoadTypes({
  orgId,
  loadTypes,
  apiService,
  warehousesKeyedByExternalId
}: LoadTypeImportParams) {
  const firstWarehouseKey: string = Object.keys(warehousesKeyedByExternalId)[0];
  const hqId =
    warehousesKeyedByExternalId[
      !isUUID(firstWarehouseKey) ? parseInt(firstWarehouseKey) : firstWarehouseKey
    ].externalHeadquartersId;
  const existingLoadTypesRes = await apiService.getLoadTypes();
  const existingLoadTypes = existingLoadTypesRes.data ?? [];
  const loadTypeIdsKeyedByExternalId: { [key: string]: string } = {};
  const loadTypesReport = [];
  if (loadTypes.length) {
    for (const loadType of loadTypes) {
      loadType.name = cleanWhiteSpace(loadType.name);
      const alreadyImported =
        (loadType?.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE ||
          loadType?.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE) &&
        loadType?.id;
      if (alreadyImported) {
        loadType.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
        loadTypesReport.push(loadType);
        loadTypeIdsKeyedByExternalId[loadType.externalId] = loadType.id as string;
        continue;
      }
      const loadTypeWarehouse = warehousesKeyedByExternalId[
        loadType.externalWarehouseId
      ] as INovaWarehouse;
      // TODO: Seeing pattern with checking existing entities here - explore abstraction
      const isOrgLevel = hqId === loadType.externalWarehouseId || !loadType.externalWarehouseId;
      const existingLoadType = existingLoadTypes.find(existingLT => {
        const doNameAndDirectionMatch =
          loadType.name === cleanWhiteSpace(existingLT.name) &&
          loadType.direction === existingLT.direction;

        if (isOrgLevel) {
          // Return the loadtype that has the same name and NO warehouseId
          // In this case it would be a org loadtype
          return doNameAndDirectionMatch && !existingLT.warehouseId;
        }

        // In this case we will match an existing nova warehouse
        // only if it has a valid warehouseId and the same warehouseId
        // as the imported one
        const isLocationMatch =
          loadTypeWarehouse &&
          Boolean(existingLT.warehouseId) &&
          existingLT.warehouseId === loadTypeWarehouse.id;
        return doNameAndDirectionMatch && isLocationMatch;
      });
      if (existingLoadType) {
        loadType.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
        loadType.id = existingLoadType.id;
        loadType.warehouseId = existingLoadType.warehouseId;
        loadType.orgId = existingLoadType.orgId;
        loadTypesReport.push(loadType);
        if (loadType.externalId && existingLoadType.id) {
          loadTypeIdsKeyedByExternalId[loadType.externalId] = existingLoadType.id;
        }
        continue;
      }

      loadType.orgId = orgId;
      try {
        if (!isOrgLevel) {
          if (!loadTypeWarehouse) {
            throw new Error(
              `Load Type ${loadType.name} (id: ${loadType.externalId}) is not assigned to an valid warehouse`
            );
          }
          loadType.warehouseId = loadTypeWarehouse.id as string;
        }
        const response = await apiService.createLoadType(loadType as INovaLoadType);
        const newLoadType = response.data[0] as unknown as INovaLoadType;
        loadType.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
        loadType.id = newLoadType.id;
        loadTypesReport.push(loadType);
        if (loadType.externalId && newLoadType.id) {
          loadTypeIdsKeyedByExternalId[loadType.externalId] = newLoadType.id;
        }
      } catch (e: any) {
        loadType.wasImportedIntoNova = getFailureStatusMessage({ e });
        loadTypesReport.unshift(loadType);
      }
    }
  }

  return { loadTypeIdsKeyedByExternalId, loadTypesReport };
}

async function importDocks({
  orgId,
  docks,
  apiService,
  loadTypeIdsKeyedByExternalId,
  warehousesKeyedByExternalId
}: DockImportParams) {
  const existingDocksRes = await apiService.getDocks();
  const existingDocks = existingDocksRes.data ?? [];
  const docksKeyedByExternalId: { [key: string]: INovaDock } = {};
  const docksReport = [];
  if (docks.length) {
    for (const dock of docks) {
      dock.name = cleanWhiteSpace(dock.name);
      if (
        (dock?.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE ||
          dock?.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE) &&
        dock?.id
      ) {
        dock.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
        docksKeyedByExternalId[dock.externalId] = dock;
        docksReport.push(dock);
        continue;
      }
      // TODO: Seeing pattern with checking existing entities here - explore abstraction
      const existingDock = existingDocks.find(existingDock => {
        const dockWarehouseId = warehousesKeyedByExternalId[dock.externalWarehouseId]?.id;
        return (
          dock.name.toLowerCase() === cleanWhiteSpace(existingDock.name).toLowerCase() &&
          dockWarehouseId === existingDock.warehouseId
        );
      });
      if (existingDock) {
        dock.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
        dock.id = existingDock.id;
        docksReport.push(dock);
        if (dock.externalId && existingDock.id) {
          docksKeyedByExternalId[dock.externalId] = existingDock as INovaDock;
        }
        continue;
      }
      dock.allowOverBooking = dock.isMapts || false;
      dock.schedule = JSON.parse(dock.schedule as string);
      const dockLoadTypeIds: string[] = [];
      const loadTypeIds: string[] = JSON.parse(dock.externalLoadTypeIds);
      if (loadTypeIds.length === 0) {
        dock.wasImportedIntoNova = getFailureStatusMessage({
          message:
            'A minimum of 1 Load Type is required to create a Dock. If this dock has load types assigned, make sure docks are shared between locations (#6 on location settings)'
        });
        dock.schedule = JSON.stringify(dock.schedule);
        docksReport.unshift(dock);
        continue;
      }
      loadTypeIds
        .filter((loadTypeId, index) => {
          return loadTypeIds.indexOf(loadTypeId) === index;
        })
        .forEach(loadTypeId => {
          dockLoadTypeIds.push(loadTypeIdsKeyedByExternalId[loadTypeId]);
        });
      dock.loadTypeIds = Array.from(new Set(dockLoadTypeIds));
      Object.keys(dock).forEach(key => {
        const propertyVal = getProperty(dock, key);
        if (typeof propertyVal !== 'boolean' && propertyVal !== 0 && !propertyVal) {
          deleteProperty(dock, key);
        }
      });
      const warehouseId = warehousesKeyedByExternalId[dock.externalWarehouseId]?.id as string;
      if (warehouseId) {
        try {
          dock.warehouseId = warehouseId;
          dock.orgId = orgId;
          const originalSchedule = dock.schedule;
          // Docks should be created with an open schedule to accommodate booking outside of operating hours
          // Using this instead of null - it saves as "null" in the DB and that makes me nervous
          dock.schedule = JSON.parse('{"version": 1}');
          const response = await apiService.createDock(dock as INovaDock);
          const newDock = response.data[0] as unknown as INovaDock;
          dock.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;

          // Set schedule back to original so we can update after appointments are created
          dock.schedule = originalSchedule;
          dock.schedule = JSON.stringify(dock.schedule);

          if (dock.externalId && newDock.id) {
            docksKeyedByExternalId[dock.externalId] = newDock;
            dock.id = newDock.id;
          }
          docksReport.push(dock);
        } catch (e: any) {
          dock.wasImportedIntoNova = getFailureStatusMessage({ e });
          dock.schedule = JSON.stringify(dock.schedule);
          docksReport.unshift(dock);
        }
      } else {
        dock.wasImportedIntoNova = getFailureStatusMessage({
          message: `Dock's Warehouse (${dock.externalWarehouseId}) failed to import - see Warehouses tab for details`
        });
        dock.schedule = JSON.stringify(dock.schedule);
        docksReport.unshift(dock);
      }
    }
  }
  return { docksKeyedByExternalId, docksReport };
}

async function updateWarehouseCustomFields(
  apiService: NovaApiService,
  warehouseId: string,
  customFields: GenericArrayOfObjects
) {
  if (typeof customFields === 'string') {
    try {
      customFields = JSON.parse(customFields);
    } catch (e) {
      customFields = [];
    }
  }
  await apiService.updateWarehouse(warehouseId, {
    customApptFieldsTemplate: customFields?.length < 1 ? null : customFields
  });
}

async function updateMaptsDockSchedule(
  apiService: NovaApiService,
  dock: INovaDock,
  schedule: INovaSchedule
) {
  await apiService.updateDock(dock.id as string, {
    schedule
  });
}

const getAppointmentCcEmails = (
  appointment: { [key: string]: any },
  appointmentEmails: string[]
) => {
  return [...appointmentEmails.slice(1), ...emailToFormattedArray(appointment.ccEmails ?? [])];
};

const setOrgs = async () => {
  if (state.orgSearch) {
    try {
      state.isLoading.orgs = true;
      const response = await novaApiService.getOrgs({
        s: {
          name: {
            $contL: state.orgSearch
          }
        },
        limit: 15
      });
      if (response?.data) {
        state.orgs = response.data;
      }
    } finally {
      state.isLoading.orgs = false;
    }
  } else {
    state.orgs = [];
  }
};

const closeGateDialog = () => {
  if (!state.isLoading.import) {
    state.shouldShowGate = false;
    state.confirmTextInput = '';
  }
};

const setOrgUsers = async () => {
  if (state.selectedOrg) {
    state.isLoading.users = true;
    try {
      const response = await novaApiService.getUsers({
        s: {
          $and: [
            {
              orgId: {
                $eq: state.selectedOrg.id
              }
            },
            {
              $or: [
                {
                  role: {
                    $eq: 'role_owner'
                  }
                },
                {
                  role: {
                    $eq: 'role_admin'
                  }
                }
              ]
            }
          ]
        }
      });
      if (response?.data) {
        state.users = response.data;
      }
    } finally {
      state.isLoading.users = false;
    }
  }
};

async function inviteUsers(
  apiService: NovaApiService,
  users: any,
  existingWarehouses: GenericArrayOfObjects
) {
  const usersReport: INovaUser[] = [];
  for (const user of users) {
    let shouldSkipUserImport = false;
    if (
      ![UserRole.ADMIN, UserRole.OPERATOR, UserRole.ATTENDANT, UserRole.SPECTATOR].includes(
        user.role
      ) &&
      user.warehouseAccessList?.length > 0
    ) {
      shouldSkipUserImport = true;
      user.wasImportedIntoNova = getFailureStatusMessage({
        message: `User role of "${user.role}" does not support a warehouse access list.`
      });
      usersReport.unshift(user);
    } else {
      if (user.warehouseAccessList?.length > 0) {
        let accessListIds: string[] = [];

        const warehouseNameArr = cleanWhiteSpace(user.warehouseAccessList).split(';') as string[];
        const matchingWarehouses = existingWarehouses.filter(warehouse => {
          return warehouseNameArr.some(warehouseName => {
            return (
              warehouseName.toLowerCase().trim() === cleanWhiteSpace(warehouse.name).toLowerCase()
            );
          });
        });

        if (matchingWarehouses.length !== warehouseNameArr.length) {
          user.wasImportedIntoNova = getFailureStatusMessage({
            message: `Cannot assign user to warehouse. One or more warehouses in warehouse access list do not exist in this Org. Please check the warehouse list separator, it must be a semicolon ';'.`
          });
          shouldSkipUserImport = true;
        } else {
          accessListIds = matchingWarehouses.map(warehouse => warehouse.id);
        }

        if (shouldSkipUserImport) {
          usersReport.unshift(user);
        } else {
          user.warehouseAccessListInput = user.warehouseAccessList;
          user.warehouseAccessList = accessListIds;
        }
      }

      if (!shouldSkipUserImport) {
        try {
          await apiService.inviteUser(user);
          user.warehouseAccessList = user.warehouseAccessListInput;
          delete user.warehouseAccessListInput;
          user.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
          usersReport.push(user);
        } catch (e) {
          user.warehouseAccessList = user.warehouseAccessListInput;
          delete user.warehouseAccessListInput;
          user.wasImportedIntoNova = getFailureStatusMessage({ e });
          usersReport.unshift(user);
        }
      }
    }
  }
  return { usersReport };
}

async function createCarriers(
  apiService: NovaApiService,
  carriers: any,
  companiesKeyedByName: { [key: string]: any }
) {
  const carriersReport: INovaCarrierData[] = [];
  for (const carrier of carriers) {
    if (carrier.phone) {
      carrier.phone = String(carrier.phone);
    }
    const companyKey = Object.keys(companiesKeyedByName).find(
      company => company.toLowerCase() === carrier.company.toLowerCase()
    );
    if (companyKey) {
      // Use company ID
      carrier.companyId = companiesKeyedByName[companyKey].id;
    } else {
      // Need to create a company
      try {
        const companyRes = await apiService.createCompany(carrier.company);
        if (companyRes.data) {
          const company = companyRes.data[0];
          companiesKeyedByName[carrier.company] = company;
          carrier.companyId = company.id;
        }
      } catch (e) {
        carrier.wasImportedIntoNova = getFailureStatusMessage({
          message: `Carrier company creation failed because ${getErrorMessage(e)}`
        });
        carriersReport.unshift(carrier);
        continue;
      }
    }

    try {
      await apiService.createCarrier(carrier);
      carrier.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
      carriersReport.push(carrier);
    } catch (e) {
      let errorMessage = getErrorMessage(e);
      if (errorMessage.includes('duplicate key value violates unique constraint')) {
        errorMessage = `user with email ${carrier.email} already exists`;
      }
      carrier.wasImportedIntoNova = getFailureStatusMessage({
        message: `Carrier creation failed because ${errorMessage}`
      });
      carriersReport.unshift(carrier);
      continue;
    }
  }
  return { carriersReport };
}

const openGateDialog = async () => {
  if (!isValid() || state.isLoading.import) {
    return;
  }
  if (!state.file) return;
  const data = await state.file.arrayBuffer();
  const workbookToImport: WorkBook = read(data);
  state.importEntityCounts = IMPORT_ENTITIES.map(entityName => {
    return {
      entity: entityName,
      count: utils.sheet_to_json(workbookToImport.Sheets[entityName]).length
    };
  });
  state.shouldShowGate = true;
};

const selectedOrgName = computed(() => {
  return state.selectedOrg?.name ?? '';
});

const selectedOrgId = computed(() => {
  return state.selectedOrg?.id ?? '';
});

const selectedUserName = computed(() => {
  return state.selectedUser
    ? `${state.selectedUser?.firstName} ${state.selectedUser?.lastName}`
    : '';
});

const selectedUserRole = computed(() => {
  return state.selectedUser?.role.replace('role_', '').toUpperCase();
});

watch(
  () => state.orgSearch,
  () => {
    const debouncedFunc = debounce(() => {
      setOrgs();
    }, 500);

    debouncedFunc();
  }
);

watch(
  () => state.selectedOrg,
  () => {
    setOrgUsers();
  }
);

// VALIDATION
const fieldErrors = reactive({
  file: '',
  org: '',
  user: ''
});
const errorMessages = {
  file: 'A .xlsx file is required',
  org: 'An Org is required',
  user: 'An Org User is required'
};

const isValid = () => {
  if (!state.file) {
    fieldErrors.file = errorMessages.file;
  }
  if (!state.selectedOrg) {
    fieldErrors.org = errorMessages.org;
  }
  if (!state.selectedUser) {
    fieldErrors.user = errorMessages.user;
  }
  if (fieldErrors.file || fieldErrors.org || fieldErrors.user) {
    return false;
  }

  return true;
};

const isConfirmed = computed(() => {
  return state.confirmTextInput === CONFIRM_TEXT;
});

watch(
  () => state.file,
  file => {
    if (file) {
      fieldErrors.file = '';
    } else {
      fieldErrors.file = errorMessages.file;
    }
  }
);

watch(
  () => state.selectedOrg,
  org => {
    if (org) {
      fieldErrors.org = '';
    } else {
      fieldErrors.org = errorMessages.org;
    }
  }
);

watch(
  () => state.selectedUser,
  user => {
    if (user) {
      fieldErrors.user = '';
    } else {
      fieldErrors.user = errorMessages.user;
    }
  }
);

return (_ctx: any,_cache: any) => {
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, { class: "d-flex flex-column full-height" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(), _createBlock(_component_v_file_input, {
          key: `nova-file-import-${state.fileRenderKey}`,
          class: _normalizeClass({ text__error: fieldErrors.file }),
          "prepend-icon": "",
          "prepend-inner-icon": "mdi-attachment",
          modelValue: state.file,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.file) = $event)),
          "error-messages": fieldErrors.file,
          "onUpdate:errorMessages": _cache[1] || (_cache[1] = ($event: any) => ((fieldErrors.file) = $event)),
          "truncate-length": "15",
          required: "",
          density: "compact",
          label: "Upload .xlsx File",
          variant: "outlined"
        }, null, 8, ["class", "modelValue", "error-messages"])),
        _createVNode(_component_v_autocomplete, {
          class: _normalizeClass({ text__error: fieldErrors.org }),
          loading: state.isLoading.orgs,
          modelValue: state.selectedOrg,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.selectedOrg) = $event)),
          "error-messages": fieldErrors.org,
          "onUpdate:errorMessages": _cache[3] || (_cache[3] = ($event: any) => ((fieldErrors.org) = $event)),
          "onUpdate:search": _cache[4] || (_cache[4] = value => (state.orgSearch = value)),
          "item-title": item => item.name,
          "item-text": item => item.name,
          "item-value": item => item.id,
          items: state.orgs,
          variant: "outlined",
          density: "compact",
          clearable: "",
          placeholder: "Start typing Org name",
          "return-object": "",
          label: "Search for an Org"
        }, null, 8, ["class", "loading", "modelValue", "error-messages", "item-title", "item-text", "item-value", "items"]),
        _createVNode(_component_v_autocomplete, {
          class: _normalizeClass({ text__error: fieldErrors.user }),
          loading: state.isLoading.users,
          disabled: !state.selectedOrg,
          modelValue: state.selectedUser,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((state.selectedUser) = $event)),
          "error-messages": fieldErrors.user,
          "onUpdate:errorMessages": _cache[6] || (_cache[6] = ($event: any) => ((fieldErrors.user) = $event)),
          "item-title": item => `${item.firstName} ${item.lastName} - ${item.role}`,
          "item-text": item => `${item.firstName} ${item.lastName} - ${item.role}`,
          "item-value": item => item.id,
          items: state.users,
          variant: "outlined",
          density: "compact",
          "return-object": "",
          label: "Select a User"
        }, null, 8, ["class", "loading", "disabled", "modelValue", "error-messages", "item-title", "item-text", "item-value", "items"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_v_dialog, {
          modelValue: state.shouldShowGate,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((state.shouldShowGate) = $event)),
          persistent: "",
          scrollable: ""
        }, {
          activator: _withCtx(({}) => [
            _createVNode(PrimaryButton, {
              loading: state.isLoading.import,
              onClick: openGateDialog,
              "prepend-icon": "mdi-upload"
            }, {
              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                _createTextVNode(" Import Spreadsheet ", -1)
              ]))]),
              _: 1
            }, 8, ["loading"])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_card, {
              class: "dialog-box",
              width: "900"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, { class: "px-6 error" }, {
                  default: _withCtx(() => [
                    _cache[11] || (_cache[11] = _createElementVNode("span", { class: "headline" }, "Confirm Data Import", -1)),
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      class: "pa-3 error",
                      icon: "",
                      elevation: 0,
                      onClick: closeGateDialog
                    }, {
                      default: _withCtx(() => [
                        (!state.isLoading.import)
                          ? (_openBlock(), _createBlock(_component_v_icon, { key: 0 }, {
                              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                _createTextVNode("mdi-close-circle", -1)
                              ]))]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, { class: "px-2 pt-2" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_container, { fluid: "" }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_3, [
                          _cache[12] || (_cache[12] = _createTextVNode(" You are about to import the following data into ", -1)),
                          _createElementVNode("strong", null, _toDisplayString(selectedOrgName.value) + " (" + _toDisplayString(selectedOrgId.value) + ")", 1),
                          _cache[13] || (_cache[13] = _createTextVNode(" as Org user ", -1)),
                          _createElementVNode("strong", null, _toDisplayString(selectedUserName.value), 1),
                          _createTextVNode(" (" + _toDisplayString(selectedUserRole.value) + ") ", 1)
                        ]),
                        _createElementVNode("table", null, [
                          _cache[14] || (_cache[14] = _createElementVNode("thead", null, null, -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.importEntityCounts, (entityCount) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: `count-${entityCount.name}`
                              }, [
                                _createElementVNode("td", _hoisted_4, [
                                  _createElementVNode("strong", null, _toDisplayString(entityCount.entity) + ":", 1)
                                ]),
                                _createElementVNode("td", _hoisted_5, _toDisplayString(entityCount.count), 1)
                              ]))
                            }), 128))
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_6, [
                          _createElementVNode("div", null, [
                            _createVNode(_component_v_text_field, {
                              label: `TYPE 'CONFIRM' TO CONTINUE`,
                              placeholder: "CONFIRM",
                              modelValue: state.confirmTextInput,
                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((state.confirmTextInput) = $event)),
                              variant: "outlined",
                              density: "compact"
                            }, null, 8, ["modelValue"])
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    (!state.isLoading.import)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 0,
                          onClick: closeGateDialog
                        }, {
                          default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                            _createTextVNode("Nevermind", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_component_v_btn, {
                      disabled: !isConfirmed.value,
                      loading: state.isLoading.import,
                      class: _normalizeClass({ primary: isConfirmed.value }),
                      onClick: importData
                    }, {
                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode(" Confirm ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled", "loading", "class"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(ProgressDialog, {
          title: "Importing Data Into Nova",
          "should-show-progress-dialog": progress.shouldShowProgressDialog,
          "current-step-message": progress.currentStepMessage,
          "current-perc": progress.currentPerc
        }, null, 8, ["should-show-progress-dialog", "current-step-message", "current-perc"])
      ])
    ]),
    _: 1
  }))
}
}

})